import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import type { TopAgentListItem } from '@/types';
import type { TopAgentFilterValue } from '../utils/adsSearchParams';

import styles from './TopAgentFilter.module.scss';
import { ReactComponent as CaretDown } from './caretDown.svg';

interface TopAgentOption {
  label: string;
  value?: 'all' | string;
}

interface TopAgentFilterProps {
  error: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  onChange: (value: TopAgentFilterValue) => void;
  topAgents: TopAgentListItem[];
  value: TopAgentFilterValue;
}

const allTopAgentsOption: TopAgentOption = {
  label: 'All Top Agents',
  value: 'all',
};

export const TopAgentFilter = ({
  error,
  isLoaded,
  isLoading,
  onChange,
  topAgents,
  value,
}: TopAgentFilterProps) => {
  const agentOptions = topAgents.map<TopAgentOption>(({ id, name }) => ({
    label: name,
    value: id,
  }));
  const hasSelectedAgent =
    value !== undefined && value !== 'all' && topAgents.some(({ id }) => id === value);
  const missingSelectedAgentOption: TopAgentOption | undefined =
    value !== undefined && value !== 'all' && !hasSelectedAgent
      ? { label: 'Selected Top Agent', value }
      : undefined;
  const options = [
    allTopAgentsOption,
    ...(missingSelectedAgentOption ? [missingSelectedAgentOption] : []),
    ...agentOptions,
  ];
  const selectedOption =
    value === undefined ? null : options.find((option) => option.value === value) ?? null;
  const helperText = error
    ? 'Top Agents are unavailable'
    : isLoaded && topAgents.length === 0
      ? 'No Top Agents available'
      : undefined;

  return (
    <div className={styles.container} data-testid='top-agent-filter'>
      <Autocomplete<TopAgentOption, false, false, false>
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selectedValue) => option.value === selectedValue.value}
        loading={isLoading}
        noOptionsText={error ? 'Unable to load Top Agents' : 'No Top Agents found'}
        onChange={(_, option) => onChange(option?.value)}
        options={options}
        popupIcon={<CaretDown className={styles.popupIcon} data-testid='top-agent-popup-icon' />}
        value={selectedOption}
        renderOption={(props, option) => (
          <li {...props} key={`${option.value ?? 'listings'}-${option.label}`}>
            <span className={styles.optionText}>{option.label}</span>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            helperText={helperText}
            placeholder='Filter by Top Agent'
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Top Agents',
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color='inherit' size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
};
